import { createClient } from '@supabase/supabase-js';
import type {
  Action,
  Audit,
  AuditCommitment,
  AuditFinding,
  Company,
  Document,
  Indicator,
  ImprovementFollowup,
  ImprovementPlan,
  Measurement,
  Person,
  ProcessSummary,
  Profile,
  Role,
  Risk,
  Workspace,
} from '../types/domain';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const apiBaseUrl = (import.meta.env.VITE_API_URL ?? '').trim();
const adminApiBase = `${apiBaseUrl.replace(/\/$/, '')}/admin1005897067`;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Las variables VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY deben estar definidas.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

async function fetchApi<T>(path: string, options: RequestInit = {}) {
  const url = `${apiBaseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
  const { data: { session } } = await supabase.auth.getSession();
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      ...(options.headers || {}),
    },
    ...options,
  });

  let body: any = null;
  try {
    body = await response.json();
  } catch {
    // ignore parse errors, body may be empty
  }

  if (!response.ok) {
    const message = body?.error?.message || body?.error || body?.message || response.statusText;
    throw new Error(message);
  }

  return body as T;
}

export type SignInResult = {
  data: { session: { access_token: string } | null };
  error: Error | null;
};

export async function signIn(email: string, password: string) {
  return supabase.auth.signInWithPassword({ email, password });
}

export async function signOut() {
  return supabase.auth.signOut();
}

export async function getCurrentSession() {
  return supabase.auth.getSession();
}

export async function getCurrentProfile(): Promise<Profile | null> {
  const session = await getCurrentSession();
  const token = session.data.session?.access_token;
  if (!token) return null;

  return fetchApi<Profile>('/api/profile', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export async function getWorkspaces(): Promise<Workspace[]> {
  return fetchApi<Workspace[]>('/api/workspaces');
}

export async function createWorkspace(name: string, plan = 'Standard', status = 'active'): Promise<Workspace> {
  return fetchApi<Workspace>('/api/workspaces', {
    method: 'POST',
    body: JSON.stringify({ name, plan, status }),
  });
}

export async function getProcesses(workspaceId: string): Promise<ProcessSummary[]> {
  return fetchApi<ProcessSummary[]>(`/api/workspaces/${workspaceId}/processes`);
}

export async function createProcess(workspaceId: string, process: Partial<ProcessSummary>): Promise<ProcessSummary> {
  return fetchApi<ProcessSummary>(`/api/workspaces/${workspaceId}/processes`, {
    method: 'POST',
    body: JSON.stringify(process),
  });
}

export async function updateProcess(workspaceId: string, processId: string, process: Partial<ProcessSummary>): Promise<ProcessSummary> {
  return fetchApi<ProcessSummary>(`/api/workspaces/${workspaceId}/processes/${processId}`, {
    method: 'PATCH',
    body: JSON.stringify(process),
  });
}

export async function getCompanies(workspaceId: string): Promise<Company[]> {
  return fetchApi<Company[]>(`/api/workspaces/${workspaceId}/companies`);
}

export async function getIndicators(workspaceId: string): Promise<Indicator[]> {
  return fetchApi<Indicator[]>(`/api/workspaces/${workspaceId}/indicators`);
}

export async function getMeasurements(indicatorId: string): Promise<Measurement[]> {
  return fetchApi<Measurement[]>(`/api/indicators/${indicatorId}/measurements`);
}

export async function getRisks(workspaceId: string): Promise<Risk[]> {
  return fetchApi<Risk[]>(`/api/workspaces/${workspaceId}/risks`);
}

export async function getActions(workspaceId: string): Promise<Action[]> {
  return fetchApi<Action[]>(`/api/workspaces/${workspaceId}/actions`);
}

export async function getImprovementPlans(workspaceId: string): Promise<ImprovementPlan[]> {
  return fetchApi<ImprovementPlan[]>(`/api/workspaces/${workspaceId}/improvement-plans`);
}

export async function getImprovementFollowups(planId: string): Promise<ImprovementFollowup[]> {
  return fetchApi<ImprovementFollowup[]>(`/api/improvement-plans/${planId}/followups`);
}

export async function getDocuments(workspaceId: string): Promise<Document[]> {
  return fetchApi<Document[]>(`/api/workspaces/${workspaceId}/documents`);
}

export async function getAudits(workspaceId: string): Promise<Audit[]> {
  return fetchApi<Audit[]>(`/api/workspaces/${workspaceId}/audits`);
}

export async function getAuditFindings(auditId: string): Promise<AuditFinding[]> {
  return fetchApi<AuditFinding[]>(`/api/audits/${auditId}/findings`);
}

export async function getAuditCommitments(findingId: string): Promise<AuditCommitment[]> {
  return fetchApi<AuditCommitment[]>(`/api/audit-findings/${findingId}/commitments`);
}

export async function getPeople(workspaceId: string): Promise<Person[]> {
  return fetchApi<Person[]>(`/api/workspaces/${workspaceId}/people`);
}

export async function getRoles(workspaceId: string): Promise<Role[]> {
  return fetchApi<Role[]>(`/api/workspaces/${workspaceId}/roles`);
}

export async function createCompany(workspaceId: string, company: Partial<Company>): Promise<Company> {
  return fetchApi<Company>(`/api/workspaces/${workspaceId}/companies`, {
    method: 'POST',
    body: JSON.stringify(company),
  });
}

export async function updateCompany(workspaceId: string, companyId: string, company: Partial<Company>): Promise<Company> {
  return fetchApi<Company>(`/api/workspaces/${workspaceId}/companies/${companyId}`, {
    method: 'PATCH',
    body: JSON.stringify(company),
  });
}

export async function createRisk(workspaceId: string, risk: Partial<Risk>): Promise<Risk> {
  return fetchApi<Risk>(`/api/workspaces/${workspaceId}/risks`, {
    method: 'POST',
    body: JSON.stringify(risk),
  });
}

export async function updateRisk(workspaceId: string, riskId: string, risk: Partial<Risk>): Promise<Risk> {
  return fetchApi<Risk>(`/api/workspaces/${workspaceId}/risks/${riskId}`, {
    method: 'PATCH',
    body: JSON.stringify(risk),
  });
}

export async function createAction(workspaceId: string, action: Partial<Action>): Promise<Action> {
  return fetchApi<Action>(`/api/workspaces/${workspaceId}/actions`, {
    method: 'POST',
    body: JSON.stringify(action),
  });
}

export async function updateAction(workspaceId: string, actionId: string, action: Partial<Action>): Promise<Action> {
  return fetchApi<Action>(`/api/workspaces/${workspaceId}/actions/${actionId}`, {
    method: 'PATCH',
    body: JSON.stringify(action),
  });
}

export async function createImprovementPlan(workspaceId: string, plan: Partial<ImprovementPlan>): Promise<ImprovementPlan> {
  return fetchApi<ImprovementPlan>(`/api/workspaces/${workspaceId}/improvement-plans`, {
    method: 'POST',
    body: JSON.stringify(plan),
  });
}

export async function updateImprovementPlan(workspaceId: string, planId: string, plan: Partial<ImprovementPlan>): Promise<ImprovementPlan> {
  return fetchApi<ImprovementPlan>(`/api/workspaces/${workspaceId}/improvement-plans/${planId}`, {
    method: 'PATCH',
    body: JSON.stringify(plan),
  });
}

export async function createImprovementFollowup(planId: string, followup: Partial<ImprovementFollowup>): Promise<ImprovementFollowup> {
  return fetchApi<ImprovementFollowup>(`/api/improvement-plans/${planId}/followups`, {
    method: 'POST',
    body: JSON.stringify(followup),
  });
}

export async function createDocument(workspaceId: string, document: Partial<Document>): Promise<Document> {
  return fetchApi<Document>(`/api/workspaces/${workspaceId}/documents`, {
    method: 'POST',
    body: JSON.stringify(document),
  });
}

export async function updateDocument(workspaceId: string, documentId: string, document: Partial<Document>): Promise<Document> {
  return fetchApi<Document>(`/api/workspaces/${workspaceId}/documents/${documentId}`, {
    method: 'PATCH',
    body: JSON.stringify(document),
  });
}

export async function deleteDocument(workspaceId: string, documentId: string): Promise<void> {
  return fetchApi<void>(`/api/workspaces/${workspaceId}/documents/${documentId}`, {
    method: 'DELETE',
  });
}

export async function createAudit(workspaceId: string, audit: Partial<Audit>): Promise<Audit> {
  return fetchApi<Audit>(`/api/workspaces/${workspaceId}/audits`, {
    method: 'POST',
    body: JSON.stringify(audit),
  });
}

export async function updateAudit(workspaceId: string, auditId: string, audit: Partial<Audit>): Promise<Audit> {
  return fetchApi<Audit>(`/api/workspaces/${workspaceId}/audits/${auditId}`, {
    method: 'PATCH',
    body: JSON.stringify(audit),
  });
}

export async function createAuditFinding(auditId: string, finding: Partial<AuditFinding>): Promise<AuditFinding> {
  return fetchApi<AuditFinding>(`/api/audits/${auditId}/findings`, {
    method: 'POST',
    body: JSON.stringify(finding),
  });
}

export async function updateAuditFinding(auditId: string, findingId: string, finding: Partial<AuditFinding>): Promise<AuditFinding> {
  return fetchApi<AuditFinding>(`/api/audits/${auditId}/findings/${findingId}`, {
    method: 'PATCH',
    body: JSON.stringify(finding),
  });
}

export async function createAuditCommitment(findingId: string, commitment: Partial<AuditCommitment>): Promise<AuditCommitment> {
  return fetchApi<AuditCommitment>(`/api/audit-findings/${findingId}/commitments`, {
    method: 'POST',
    body: JSON.stringify(commitment),
  });
}

export async function updateAuditCommitment(findingId: string, commitmentId: string, commitment: Partial<AuditCommitment>): Promise<AuditCommitment> {
  return fetchApi<AuditCommitment>(`/api/audit-findings/${findingId}/commitments/${commitmentId}`, {
    method: 'PATCH',
    body: JSON.stringify(commitment),
  });
}

export async function createPerson(workspaceId: string, person: Partial<Person>): Promise<Person> {
  return fetchApi<Person>(`/api/workspaces/${workspaceId}/people`, {
    method: 'POST',
    body: JSON.stringify(person),
  });
}

export async function updatePerson(workspaceId: string, personId: string, person: Partial<Person>): Promise<Person> {
  return fetchApi<Person>(`/api/workspaces/${workspaceId}/people/${personId}`, {
    method: 'PATCH',
    body: JSON.stringify(person),
  });
}

export async function createRole(workspaceId: string, role: Partial<Role>): Promise<Role> {
  return fetchApi<Role>(`/api/workspaces/${workspaceId}/roles`, {
    method: 'POST',
    body: JSON.stringify(role),
  });
}

export async function updateRole(workspaceId: string, roleId: string, role: Partial<Role>): Promise<Role> {
  return fetchApi<Role>(`/api/workspaces/${workspaceId}/roles/${roleId}`, {
    method: 'PATCH',
    body: JSON.stringify(role),
  });
}

export async function deleteRole(workspaceId: string, roleId: string): Promise<void> {
  return fetchApi<void>(`/api/workspaces/${workspaceId}/roles/${roleId}`, {
    method: 'DELETE',
  });
}

export async function createIndicator(workspaceId: string, indicator: Partial<Indicator>): Promise<Indicator> {
  return fetchApi<Indicator>(`/api/workspaces/${workspaceId}/indicators`, {
    method: 'POST',
    body: JSON.stringify(indicator),
  });
}

export async function createMeasurement(indicatorId: string, measurement: Partial<Measurement>): Promise<Measurement> {
  return fetchApi<Measurement>(`/api/indicators/${indicatorId}/measurements`, {
    method: 'POST',
    body: JSON.stringify(measurement),
  });
}
